import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { reportsApi, adminApi, emailFlowApi } from '../services/api';
import { useAuthStore } from '../context/auth.store';
import toast from 'react-hot-toast';
import { format, formatDistanceToNow } from 'date-fns';
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, ResponsiveContainer } from 'recharts';
import OneDriveExplorerPage from './OneDriveExplorerPage';

const FILE_EMOJI = { pdf:'📄', xlsx:'📊', xls:'📊', xlsm:'📊', csv:'📋' };

const StatCard = ({ label, value, icon, color }) => (
  <div className="stat-card" style={{ display: 'flex', flexDirection: 'column', padding: '16px', background: 'white', borderRadius: '12px', border: '1px solid var(--border)', boxShadow: 'var(--shadow-sm)' }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
      <div className="stat-icon" style={{ background: color+'22', padding: '8px', borderRadius: '8px', display: 'flex' }}>
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2"
          dangerouslySetInnerHTML={{__html: icon}}/>
      </div>
      <div className="stat-label" style={{ fontSize: '13px', color: 'var(--text-secondary)', fontWeight: 500 }}>{label}</div>
    </div>
    <div className="stat-val" style={{ fontSize: '24px', fontWeight: 700, color: 'var(--text-primary)', fontFamily: 'var(--font-display)' }}>{value}</div>
  </div>
);

function ReportCard({ report, onClick }) {
  const emoji = FILE_EMOJI[report.fileType?.toLowerCase()] ?? '📁';
  const isPdf = report.fileType?.toLowerCase() === 'pdf';
  const timeAgo = report.lastModified
    ? formatDistanceToNow(new Date(report.lastModified), { addSuffix: true })
    : '';

  return (
    <div className="report-card" onClick={() => onClick(report)}>
      <div className={`report-type-icon ${isPdf ? 'type-pdf' : 'type-xlsx'}`}>
        {emoji}
      </div>
      <div className={`category-badge cat-${report.category}`}>{report.category}</div>
      <div className="report-name">{report.name}</div>
      <div className="report-meta">{timeAgo} · {report.sizeFormatted ?? '—'}</div>
      {report.mappedFolderName && (
        <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 10, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          📁 {report.mappedFolderName}
        </div>
      )}
      {/* Single open button — no download */}
      <button className="btn-view" style={{ width: '100%', padding: '7px 0', borderRadius: 'var(--radius-sm)', cursor: 'pointer', border: '1px solid var(--border)', background: 'transparent', color: 'var(--text-secondary)', fontSize: 12, fontWeight: 500, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5, transition: 'all 0.15s', fontFamily: 'var(--font)' }}
        onClick={e => { e.stopPropagation(); onClick(report); }}>
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
        Open report
      </button>
    </div>
  );
}

export default function DashboardPage() {
  const navigate   = useNavigate();
  const { user }   = useAuthStore();

  const isAdminOrManager = ['ADMIN', 'MANAGER'].includes(user?.role);

  const [reports, setReports]             = useState([]);
  const [categories, setCategories]       = useState([]);
  const [loading, setLoading]             = useState(true);
  const [search, setSearch]               = useState('');
  const [activeCategory, setActiveCategory] = useState('all');
  const [syncStatus, setSyncStatus]       = useState(null);
  const [syncing, setSyncing]             = useState(false);
  const [viewMode, setViewMode]           = useState(() => localStorage.getItem('px_dash_view') || 'list');

  const handleSetViewMode = (v) => {
    setViewMode(v);
    localStorage.setItem('px_dash_view', v);
  };

  const [clientFilter, setClientFilter]     = useState('all');
  const [dateFilter, setDateFilter]         = useState('all');

  const loadReports = useCallback(async () => {
    try {
      const params = {};
      if (activeCategory !== 'all') params.category = activeCategory;
      if (search) params.search = search;
      const [rRes, cRes] = await Promise.all([reportsApi.list(params), reportsApi.categories()]);
      let allReports = rRes.data.reports;

      if (user?.email) {
        try {
          const flowRes = await emailFlowApi.byEmail(user.email);
          const flowReports = flowRes.data.map(f => ({
            ...f,
            isFlow: true,
            category: 'Email Flow',
            fileType: f.reportFileName?.split('.').pop() || 'pdf',
            sizeFormatted: f.frequency ? `Flow (${f.frequency})` : 'Flow',
            lastModified: f.updatedAt,
            mappedFolderName: f.onedriveFolderPath,
          }));
          allReports = [...allReports, ...flowReports];
        } catch (e) {
          console.error("Failed to load email flows", e);
        }
      }

      setReports(allReports);
      setCategories(cRes.data);
    } catch { toast.error('Failed to load reports'); }
    finally { setLoading(false); }
  }, [activeCategory, search]);

  const extractClient = (report) => {
    if (report.mappedFolderName) return report.mappedFolderName.trim();
    if (report.name) return report.name.split(' ')[0].split('-')[0].trim();
    return 'Unknown';
  };

  const clientOptions = React.useMemo(() => {
    const clients = new Set(reports.map(extractClient));
    return Array.from(clients).sort();
  }, [reports]);

  const filteredReports = React.useMemo(() => {
    return reports.filter(r => {
      // Filter by Client
      if (clientFilter !== 'all') {
        if (extractClient(r) !== clientFilter) return false;
      }
      
      // Filter by Date (lastModified)
      if (dateFilter !== 'all') {
        const d = new Date(r.lastModified);
        const now = new Date();
        if (dateFilter === 'today') {
          if (d.toDateString() !== now.toDateString()) return false;
        } else if (dateFilter === 'week') {
          const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
          if (d < weekAgo) return false;
        } else if (dateFilter === 'month') {
          if (d.getMonth() !== now.getMonth() || d.getFullYear() !== now.getFullYear()) return false;
        } else if (dateFilter.includes('-')) {
          const filterDate = new Date(dateFilter);
          if (d.toDateString() !== filterDate.toDateString()) return false;
        }
      }
      return true;
    });
  }, [reports, clientFilter, dateFilter]);

  const [adminStats, setAdminStats] = useState(null);

  const loadAdminStats = useCallback(async () => {
    if (!['ADMIN', 'MANAGER'].includes(user?.role)) return;
    try {
      const res = await adminApi.stats();
      setAdminStats(res.data);
    } catch (e) {
      console.warn("Failed to load admin stats:", e.message);
    }
  }, [user?.role]);

  const loadSyncStatus = useCallback(async () => {
    if (!['ADMIN', 'MANAGER'].includes(user?.role)) return;
    try { const r = await adminApi.syncStatus(); setSyncStatus(r.data); } catch {}
  }, [user?.role]);

  useEffect(() => { loadReports(); }, [loadReports]);
  useEffect(() => { loadSyncStatus(); }, [loadSyncStatus]);
  useEffect(() => { loadAdminStats(); }, [loadAdminStats]);

  const dashboardStats = React.useMemo(() => {
    const now = new Date();
    const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
    const newThisWeek = reports.filter(r => new Date(r.lastModified) > weekAgo).length;
    const totalClients = new Set(reports.map(extractClient)).size;
    
    // Compute Category Data for Charts
    const categoryCounts = {};
    const typeCounts = {};
    
    reports.forEach(r => {
      const cat = r.category || 'Uncategorized';
      categoryCounts[cat] = (categoryCounts[cat] || 0) + 1;
      
      let type = r.fileType?.toUpperCase() || 'UNKNOWN';
      if (['XLSX', 'XLS', 'XLSM', 'CSV'].includes(type)) type = 'SPREADSHEET';
      typeCounts[type] = (typeCounts[type] || 0) + 1;
    });

    const categoryData = Object.entries(categoryCounts)
      .map(([name, value]) => ({ name, value }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 5); // top 5

    const typeData = Object.entries(typeCounts).map(([name, value]) => ({ name, value }));

    return {
      total: reports.length,
      newThisWeek,
      clients: totalClients,
      categories: categories.length,
      categoryData,
      typeData
    };
  }, [reports, categories]);

  const [portalTarget, setPortalTarget] = useState(null);
  const [leftPortalTarget, setLeftPortalTarget] = useState(null);
  useEffect(() => { 
    setPortalTarget(document.getElementById('topbar-portal-target'));
    setLeftPortalTarget(document.getElementById('topbar-portal-target-left'));
  }, []);

  const handleSync = async () => {
    setSyncing(true);
    try {
      const res = await adminApi.triggerSync();
      toast.success(res.data.message);
      await loadReports(); await loadSyncStatus();
    } catch (err) { toast.error('Sync failed: ' + (err.response?.data?.error ?? 'Unknown error')); }
    finally { setSyncing(false); }
  };

  const syncTime = syncStatus?.lastSyncAt
    ? format(new Date(syncStatus.lastSyncAt), 'PPp')
    : 'Never';

  return (
    <>
      {/* Title via Portal */}
      {leftPortalTarget && createPortal(
        <div className="page-title" style={{ margin: 0, paddingLeft: '8px' }}>My Reports</div>,
        leftPortalTarget
      )}

      <div className="page-body">
        {/* OneDrive sync bar via Portal */}
        {['ADMIN', 'MANAGER'].includes(user?.role) && portalTarget && createPortal(
          <div className="od-bar" style={{ marginBottom: 0, padding: '4px 12px', border: 'none', background: 'transparent', marginLeft: 'auto', gap: '12px' }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#F97316" strokeWidth="2"><path d="M18 10h-1.26A8 8 0 109 20h9a5 5 0 000-10z"/></svg>
            <span style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
              <strong style={{ color: 'var(--text-primary)' }}>{syncStatus?.reportCount ?? reports.length} reports</strong> · Last synced: {syncTime}
            </span>
            <div className={`od-status ${syncStatus?.status === 'ok' ? 'od-ok' : 'od-syncing'}`}>
              {syncStatus?.status === 'ok'
                ? <><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="20 6 9 17 4 12"/></svg> Synced</>
                : (syncStatus?.status ?? 'Checking…')}
            </div>
            <button className="od-btn" onClick={handleSync} disabled={syncing}>
              {syncing ? 'Syncing…' : '↻ Sync now'}
            </button>
          </div>,
          portalTarget
        )}

        {/* Analytics Section */}
        {!loading && reports.length > 0 && (
          <>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '16px', marginBottom: '16px' }}>
              <StatCard label="Total Reports" value={dashboardStats.total} color="#059669" icon='<path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/>' />
              <StatCard label="New This Week" value={dashboardStats.newThisWeek} color="#7C3AED" icon='<rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/>' />
              <StatCard label="Total Clients" value={dashboardStats.clients} color="#F97316" icon='<path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 00-3-3.87"/><path d="M16 3.13a4 4 0 010 7.75"/>' />
              {adminStats ? (
                <>
                  <StatCard label="Report Views" value={adminStats.stats.totalViews} color="#1D4ED8" icon='<path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/>' />
                  <StatCard label="Downloads" value={adminStats.stats.totalDownloads} color="#EF4444" icon='<path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/>' />
                  <StatCard label="Active Users" value={adminStats.stats.totalUsers} color="#10B981" icon='<path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/><circle cx="9" cy="7" r="4"/>' />
                </>
              ) : (
                <StatCard label="Categories" value={dashboardStats.categories} color="#1D4ED8" icon='<rect x="3" y="3" width="7" height="7"></rect><rect x="14" y="3" width="7" height="7"></rect><rect x="14" y="14" width="7" height="7"></rect><rect x="3" y="14" width="7" height="7"></rect>' />
              )}
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '16px', marginBottom: '24px' }}>
              <div className="card card-pad" style={{ padding: '16px', height: '270px' }}>
                <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 16 }}>Top Categories</div>
                <ResponsiveContainer width="100%" height="85%">
                  <BarChart data={dashboardStats.categoryData} margin={{ top: 0, right: 0, left: -20, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E2E8F0" />
                    <XAxis dataKey="name" tick={{ fontSize: 11 }} tickLine={false} axisLine={{ stroke: '#E2E8F0' }} />
                    <YAxis tick={{ fontSize: 11 }} tickLine={false} axisLine={false} />
                    <RechartsTooltip cursor={{ fill: '#F1F5F9' }} contentStyle={{ borderRadius: 8, border: 'none', boxShadow: 'var(--shadow-md)', fontSize: 12 }} />
                    <Bar dataKey="value" fill="#3B82F6" radius={[4, 4, 0, 0]} barSize={40} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
              <div className="card card-pad" style={{ padding: '16px', height: '270px' }}>
                <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 16 }}>File Types</div>
                <ResponsiveContainer width="100%" height="85%">
                  <PieChart>
                    <Pie data={dashboardStats.typeData} cx="50%" cy="50%" innerRadius={60} outerRadius={80} paddingAngle={2} dataKey="value" stroke="none">
                      {dashboardStats.typeData.map((entry, index) => {
                        const COLORS = ['#F97316', '#10B981', '#6366F1', '#EC4899', '#8B5CF6'];
                        return <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />;
                      })}
                    </Pie>
                    <RechartsTooltip contentStyle={{ borderRadius: 8, border: 'none', boxShadow: 'var(--shadow-md)', fontSize: 12 }} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              {adminStats?.usersByRole && (
                <div className="card card-pad" style={{ padding: '16px', height: '270px' }}>
                  <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 16 }}>User Role Distribution</div>
                  <ResponsiveContainer width="100%" height="85%">
                    <PieChart>
                      <Pie data={adminStats.usersByRole} cx="50%" cy="50%" innerRadius={48} outerRadius={68} paddingAngle={3} dataKey="count" nameKey="role" label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}>
                        {adminStats.usersByRole.map((entry, index) => {
                          const COLORS = ['#7C3AED', '#EC4899', '#10B981', '#3B82F6'];
                          return <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />;
                        })}
                      </Pie>
                      <RechartsTooltip contentStyle={{ borderRadius: 8, border: 'none', boxShadow: 'var(--shadow-md)', fontSize: 11 }} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              )}
            </div>
          </>
        )}

        {/* Side-by-Side: Reports list vs Recent Activity */}
        <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap', alignItems: 'flex-start' }}>
          <div style={{ flex: '3 1 600px', display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div className="toolbar" style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              <span className="toolbar-title" style={{ flexShrink: 0, alignSelf: 'center' }}>All reports</span>
              <div className="search-box" style={{ flex: '1 1 200px' }}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
                <input placeholder="Search reports…" value={search} onChange={e => setSearch(e.target.value)} />
              </div>
              <select className="form-input" style={{ width: 140, flexShrink: 0 }} value={clientFilter} onChange={e => setClientFilter(e.target.value)}>
                <option value="all">All Clients</option>
                {clientOptions.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
              <select className="form-input" style={{ width: 140, flexShrink: 0 }} value={dateFilter.includes('-') ? 'custom' : dateFilter} onChange={e => { if (e.target.value !== 'custom') setDateFilter(e.target.value); }}>
                <option value="all">All Dates</option>
                <option value="today">Today</option>
                <option value="week">This Week</option>
                <option value="month">This Month</option>
                {dateFilter.includes('-') && <option value="custom">Custom Date</option>}
              </select>
              <input type="date" className="form-input" style={{ width: 140, flexShrink: 0 }} value={dateFilter.includes('-') ? dateFilter : ''} onChange={e => setDateFilter(e.target.value || 'all')} title="Pick a specific date" />
              <div style={{ display: 'flex', background: 'white', borderRadius: '99px', border: '1px solid var(--border)', overflow: 'hidden', height: 40, boxShadow: 'var(--shadow-sm)' }}>
                <button style={{ padding: '0 12px', border: 'none', background: viewMode === 'grid' ? 'rgba(255,112,67,0.1)' : 'transparent', color: viewMode === 'grid' ? 'var(--accent)' : 'var(--text-muted)', cursor: 'pointer', display: 'flex', alignItems: 'center', transition: 'all 0.2s' }} onClick={() => handleSetViewMode('grid')} title="Grid View">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="7" height="7"></rect><rect x="14" y="3" width="7" height="7"></rect><rect x="14" y="14" width="7" height="7"></rect><rect x="3" y="14" width="7" height="7"></rect></svg>
                </button>
                <div style={{ width: 1, background: 'var(--border)' }}></div>
                <button style={{ padding: '0 12px', border: 'none', background: viewMode === 'list' ? 'rgba(255,112,67,0.1)' : 'transparent', color: viewMode === 'list' ? 'var(--accent)' : 'var(--text-muted)', cursor: 'pointer', display: 'flex', alignItems: 'center', transition: 'all 0.2s' }} onClick={() => handleSetViewMode('list')} title="List View">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="8" y1="6" x2="21" y2="6"></line><line x1="8" y1="12" x2="21" y2="12"></line><line x1="8" y1="18" x2="21" y2="18"></line><line x1="3" y1="6" x2="3.01" y2="6"></line><line x1="3" y1="12" x2="3.01" y2="12"></line><line x1="3" y1="18" x2="3.01" y2="18"></line></svg>
                </button>
              </div>
            </div>

            <div className="filter-tabs">
              <button className={`ftab${activeCategory === 'all' ? ' active' : ''}`} onClick={() => setActiveCategory('all')}>
                All ({filteredReports.length})
              </button>
              {categories.map(cat => (
                <button key={cat} className={`ftab${activeCategory === cat ? ' active' : ''}`} onClick={() => setActiveCategory(cat)}>
                  {cat} ({filteredReports.filter(r => r.category === cat).length})
                </button>
              ))}
            </div>

            {loading ? (
              <div style={{ display: 'flex', justifyContent: 'center', padding: '48px 0' }}><div className="spinner" /></div>
            ) : filteredReports.length === 0 ? (
              <div className="empty-state">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
                <h3>No reports found</h3>
                <p>{search || clientFilter !== 'all' || dateFilter !== 'all' ? "No results match your filters." : 'No reports have been assigned to you yet.'}</p>
              </div>
            ) : (
              <div className={viewMode === 'list' ? "report-list" : "report-grid"}>
                {filteredReports.filter(r => activeCategory === 'all' || r.category === activeCategory).map(r => (
                  <ReportCard 
                    key={r.isFlow ? `flow-${r.id}` : r.id} 
                    report={r} 
                    onClick={() => r.isFlow ? navigate(`/flows/${r.id}`) : navigate(`/reports/${r.id}`)} 
                  />
                ))}
              </div>
            )}
          </div>

          {/* Right Column: Recent activity log (Admin/Manager only) */}
          {adminStats && adminStats.recentActivity && (
            <div className="card card-pad" style={{ flex: '1 1 280px', minWidth: 260, padding: 16, background: 'white', borderRadius: 12, border: '1px solid var(--border)', boxShadow: 'var(--shadow-sm)' }}>
              <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 16, display: 'flex', alignItems: 'center', gap: 6, color: 'var(--text-primary)' }}>
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/></svg>
                <span>Recent System Activity</span>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                {adminStats.recentActivity.slice(0, 6).map(log => {
                  let actLabel = 'Performed action';
                  let actColor = 'var(--text-secondary)';
                  if (log.action === 'VIEW_REPORT') { actLabel = '👁️ Viewed'; actColor = '#2563EB'; }
                  else if (log.action === 'DOWNLOAD_REPORT') { actLabel = '📥 Downloaded'; actColor = '#16A34A'; }
                  else if (log.action === 'LOGIN') { actLabel = '🔑 Logged In'; actColor = '#D97706'; }
                  else if (log.action === 'SYNC_ONEDRIVE') { actLabel = '🔄 Synced OneDrive'; actColor = '#EA580C'; }

                  return (
                    <div key={log.id} style={{ display: 'flex', flexDirection: 'column', gap: 2, borderBottom: '1px solid var(--border)', paddingBottom: 10 }}>
                      <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-primary)' }}>{log.user?.name || 'System'}</div>
                      <div style={{ fontSize: 11, color: actColor, fontWeight: 500 }}>{actLabel}</div>
                      <div style={{ fontSize: 11, color: 'var(--text-secondary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 260 }} title={log.report?.name || log.details}>
                        {log.report?.name || log.details || '—'}
                      </div>
                      <div style={{ fontSize: 9, color: 'var(--text-muted)', alignSelf: 'flex-end', marginTop: 2 }}>
                        {formatDistanceToNow(new Date(log.createdAt), { addSuffix: true })}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
